import { useContext } from "react";
import { useNavigate } from "react-router-dom";

import { format, formatDistance } from "date-fns";
import { enGB } from "date-fns/locale";

import { ButtonSmall } from "@anwb/poncho/components/button";
import Table from "@anwb/poncho/components/table";
import { Tooltip } from "@anwb/poncho/components/tooltip";
import Typography from "@anwb/poncho/components/typography";

import EnvironmentContext from "../../../contexts/EnvironmentContext";
import NotificationContext from "../../../contexts/NotificationContext";
import { useAuthorizedRequest } from "../../../helpers/api";
import { Order, Status } from "../../../types/order";
import { BrinvestToolTip } from "../../BrinvestToolTip/BrinvestToolTip";
import NoWrap from "../../NoWrap/NoWrap";

type OrderTableProps = {
  orders: Order[];
};

function OrderTable({ orders }: OrderTableProps) {
  const { setNotification } = useContext(NotificationContext);
  const { salesforceBaseURL } = useContext(EnvironmentContext);

  const navigate = useNavigate();

  const activateOrderRequest = useAuthorizedRequest<{ status: string }>();
  const activateOrder = (orderId: string) => {
    activateOrderRequest.setRequest({
      method: "post",
      path: `/orders/${orderId}/activate`,
      onSuccess: (response) => {
        setNotification({
          title:
            response.status === "success"
              ? "Order successfully activated"
              : "Order could not be activated",
          children: response.status === "success" ? null : "School is not open on Basispoort",
          variant: "success",
        });
      },
    });
  };

  const resetFoutiefOrderRequest = useAuthorizedRequest();
  const resetFoutiefOrder = (orderId: string) => {
    resetFoutiefOrderRequest.setRequest({
      method: "post",
      path: `/orders/${orderId}/reset-foutief`,
      onSuccess: () => {
        setNotification({
          title: "Status for Order successfully reset",
          children: "Refresh the page to see the result",
          variant: "success",
        });
      },
    });
  };

  const deactivateOrderRequest = useAuthorizedRequest();
  const deactivateOrder = (orderId: string) => {
    deactivateOrderRequest.setRequest({
      method: "post",
      path: `/orders/${orderId}/deactivate`,
      onSuccess: () => {
        setNotification({
          title: "Order successfully deactivated",
          variant: "success",
        });
      },
    });
  };

  const updateStudentCountRequest = useAuthorizedRequest<{ status: string }>();
  const updateStudentCount = (orderId: string) => {
    updateStudentCountRequest.setRequest({
      method: "post",
      path: `/orders/${orderId}/update-student-count`,
      onSuccess: () => {
        setNotification({
          title: "Number of students successfully updated in Salesforce",
          variant: "success",
        });
      },
      onAxiosError: () => {
        setNotification({
          title: "Could not update number of students in Salesforce",
          variant: "error",
        });
      },
    });
  };

  return (
    <>
      <Typography variant="number">
        {orders.length} order{orders.length !== 1 ? "s" : ""} found
      </Typography>
      <Table textAlign="center">
        <Table.Header>
          <Table.Row>
            <Table.HeaderColumn>Order ID</Table.HeaderColumn>
            <Table.HeaderColumn>
              School brinvest
              <BrinvestToolTip />
            </Table.HeaderColumn>
            <Table.HeaderColumn>Product</Table.HeaderColumn>
            <Table.HeaderColumn>Status</Table.HeaderColumn>
            <Table.HeaderColumn>Error Message</Table.HeaderColumn>
            <Table.HeaderColumn>Start date</Table.HeaderColumn>
            <Table.HeaderColumn>End date</Table.HeaderColumn>
            <Table.HeaderColumn>Last modification</Table.HeaderColumn>
            <Table.HeaderColumn>Details</Table.HeaderColumn>
            <Table.HeaderColumn>Actions</Table.HeaderColumn>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {orders?.map((order) => {
            return (
              <Table.Row key={order.id}>
                <Table.Column>...{order.id.substring(10)}</Table.Column>
                <Table.Column>
                  <ButtonSmall
                    color="tertiary"
                    onClick={() => navigate(`/schools/${order.school.brinvest}`)}
                  >
                    {order.school.brinvest}
                  </ButtonSmall>
                </Table.Column>
                <Table.Column>
                  <ButtonSmall
                    color="tertiary"
                    onClick={() => navigate(`/programs/${order.product.name}`)}
                  >
                    {order.product.name}
                  </ButtonSmall>
                </Table.Column>
                <Table.Column>{order.status}</Table.Column>
                <Table.Column>{order.errorMessage}</Table.Column>
                <Table.Column>{format(new Date(order.startDate), "PP")}</Table.Column>
                <Table.Column>{format(new Date(order.endDate), "PP")}</Table.Column>
                <Table.Column title={order.salesforceLastModifiedDate.toString()}>
                  {formatDistance(new Date(order.salesforceLastModifiedDate), new Date(), {
                    locale: enGB,
                    addSuffix: true,
                    includeSeconds: true,
                  })}
                </Table.Column>
                <Table.Column>
                  {salesforceBaseURL !== undefined && (
                    <ButtonSmall
                      href={`${salesforceBaseURL}/lightning/r/Activiteit__c/${order.id}/view`}
                      color="tertiary"
                    >
                      Salesforce
                    </ButtonSmall>
                  )}
                </Table.Column>
                <Table.Column>
                  {order.status === Status.Foutief && (
                    <NoWrap>
                      <ButtonSmall color="primary" onClick={() => resetFoutiefOrder(order.id)}>
                        Reset status
                      </ButtonSmall>
                      <Tooltip>
                        Updates the order status to &apos;Nieuw&apos; or &apos;Definitief&apos;
                        based on the presence of a Verwerkersovereenkomst for the associated school,
                        allowing the order to be processed again. Note: this does <em>not</em>{" "}
                        resolve the underlying issue that caused the Order to be marked
                        &apos;Foutief&apos;.
                      </Tooltip>
                    </NoWrap>
                  )}
                  {[Status.Actief, Status.Definitief].includes(order.status) && (
                    <NoWrap>
                      <ButtonSmall color="primary" onClick={() => activateOrder(order.id)}>
                        {order.status === Status.Actief ? "Reactivate" : "Activate"}
                      </ButtonSmall>
                      <Tooltip>
                        Starts a synchronisation with Basispoort. If successful, the order status
                        will be updated to &apos;Actief&apos;.
                      </Tooltip>
                    </NoWrap>
                  )}
                  {[Status.Actief].includes(order.status) && (
                    <NoWrap>
                      <ButtonSmall color="primary" onClick={() => deactivateOrder(order.id)}>
                        Deactivate
                      </ButtonSmall>
                      <Tooltip>
                        Sets order status to &apos;Verlopen&apos;. It triggers a new synchronisation
                        to (potentially) remove access to Moodle and Beheermodule.
                      </Tooltip>
                    </NoWrap>
                  )}
                  <NoWrap>
                    <ButtonSmall
                      color="primary"
                      onClick={() => updateStudentCount(order.id)}
                      title="Update student count in Salesforce"
                    >
                      Student&nbsp;count
                    </ButtonSmall>
                    <Tooltip>
                      Updates the number of students per leerjaar in the related Salesforce
                      Activiteit.
                    </Tooltip>
                  </NoWrap>
                </Table.Column>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </>
  );
}

export default OrderTable;
